import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogOut, Home, FileText, Calendar, CreditCard, Bug, History, ChevronRight, CheckCircle2, Plus, MessageSquare, FileSignature, UserCircle } from 'lucide-react';
import XpertLogo from '../components/XpertLogo';
import LanguageToggle from '../components/LanguageToggle';
import MessagingView from '../components/MessagingView';
import { fmt } from '../lib/accountingStore';
import { apiInvoices, apiWorkorders, payments } from '../lib/api';
import { portalApi } from '../lib/portalApi';
import { useLang } from '../lib/i18n';
import { toast } from 'sonner';
import { shiftDays, todayISO, normDate, prettyDate } from '../lib/dates';

const portalNav = [
  { tkey: 'overview', icon: Home, path: '' },
  { tkey: 'book', icon: Plus, path: 'book' },
  { tkey: 'messages', icon: MessageSquare, path: 'messages' },
  { tkey: 'invoices', icon: FileText, path: 'invoices' },
  { tkey: 'history', icon: History, path: 'history' },
  { tkey: 'documents', icon: FileSignature, path: 'documents' },
  { tkey: 'profile', icon: UserCircle, path: 'profile' },
];

function PortalShell({ user, onLogout, children }) {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/portal" className="flex items-center gap-2.5">
            <XpertLogo size={36}/>
            <div><div className="font-extrabold tracking-tight text-slate-900">Xpertpests <span className="text-[#F59E0B]">{t('portal.title')}</span></div></div>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <div className="text-right hidden md:block"><div className="text-sm font-semibold text-slate-900">{user?.name}</div><div className="text-xs text-slate-500">{t('portal.customer')}</div></div>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#8BC53F] to-[#6FA82C] text-white flex items-center justify-center font-bold">{user?.name?.charAt(0) || 'C'}</div>
            <Button variant="outline" size="sm" onClick={onLogout} className="gap-1.5"><LogOut className="w-4 h-4"/>{t('portal.signOut')}</Button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-6 py-6 grid lg:grid-cols-[220px_1fr] gap-6">
        <aside className="space-y-1">
          {portalNav.map(p => (
            <NavLink key={p.tkey} end={p.path===''} to={`/portal/${p.path}`} className={({isActive}) => `flex items-center gap-3 px-4 py-2.5 rounded-lg font-medium text-sm transition ${isActive ? 'bg-[#8BC53F] text-white' : 'text-slate-700 hover:bg-slate-200/60'}`}>
              <p.icon className="w-4 h-4"/>{t('portal.nav.' + p.tkey)}
            </NavLink>
          ))}
        </aside>
        <main>{children}</main>
      </div>
    </div>
  );
}

// Everything below reads the signed-in customer's own records. The API scopes
// invoices and work orders to the customer's account, so nothing here filters
// or falls back to sample data: an empty account shows empty states.
const CLOSED = new Set(['completed', 'complete', 'done', 'cancelled', 'canceled', 'void', 'missed', 'no service', 'skipped']);
const DONE = new Set(['completed', 'complete', 'done']);
const statusOf = (r) => String(r?.status || '').trim().toLowerCase();
const isPaid = (inv) => ['paid', 'void', 'voided'].includes(statusOf(inv));
const outstanding = (inv) => {
  if (isPaid(inv) || statusOf(inv) === 'draft') return 0;
  const due = inv.amountDue ?? inv.balance;
  if (due !== undefined && due !== null && due !== '') return Number(due) || 0;
  return Math.max(0, (Number(inv.total ?? inv.amount) || 0) - (Number(inv.paid ?? inv.amountReceived) || 0));
};
const invoiceAmount = (inv) => Number(inv.total ?? inv.amount ?? inv.balance ?? 0) || 0;
const invoiceLabel = (inv) => inv.number || inv.invoiceNumber || String(inv.id || '').slice(0, 12);
const byDateDesc = (a, b) => (normDate(b.date) || '').localeCompare(normDate(a.date) || '');

function usePortalData() {
  const [state, setState] = useState({ invoices: [], jobs: [], loading: true });
  useEffect(() => {
    let live = true;
    Promise.allSettled([apiInvoices.list(), apiWorkorders.list()]).then(([inv, wo]) => {
      if (!live) return;
      setState({
        invoices: inv.status === 'fulfilled' && Array.isArray(inv.value) ? inv.value : [],
        jobs: wo.status === 'fulfilled' && Array.isArray(wo.value) ? wo.value : [],
        loading: false,
      });
    });
    return () => { live = false; };
  }, []);
  return state;
}

function Overview({ user }) {
  const { t } = useLang();
  const { invoices, jobs, loading } = usePortalData();
  const today = todayISO();
  const upcoming = jobs
    .filter((j) => !CLOSED.has(statusOf(j)) && (normDate(j.date) || '') >= today)
    .sort((a, b) => (normDate(a.date) || '').localeCompare(normDate(b.date) || ''));
  const next = upcoming[0];
  const lastDone = jobs.filter((j) => DONE.has(statusOf(j))).sort(byDateDesc)[0];
  const techName = (next && next.tech) || (lastDone && lastDone.tech) || '';
  const balance = invoices.reduce((sum, inv) => sum + outstanding(inv), 0);
  const recent = [...invoices].sort(byDateDesc).slice(0, 3);
  const account = user?.accountNumber || (invoices[0] && (invoices[0].customerAccountNumber || invoices[0].customerAccount)) || '';
  return (
    <div className="space-y-5">
      <Card className="p-6 border-slate-200 bg-gradient-to-br from-[#8BC53F] to-[#6FA82C] text-white">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-white/80 text-sm">{t('portal.welcomeBack')}</p>
            <h1 className="text-3xl font-extrabold tracking-tight">{user?.name || ''}</h1>
            <p className="text-white/80 mt-2 text-sm" data-testid="portal-next-service">
              {loading ? '…' : next
                ? <>{t('portal.nextService')} <span className="font-bold">{prettyDate(normDate(next.date))}{next.time ? ` · ${next.time}` : ''}</span></>
                : t('portal.noUpcoming')}
            </p>
          </div>
          <Bug className="w-12 h-12 text-white/30"/>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="bg-white/15 rounded-lg p-3"><div className="text-xs text-white/70">{t('portal.accountNo')}</div><div className="font-bold">{account || '—'}</div></div>
          <div className="bg-white/15 rounded-lg p-3"><div className="text-xs text-white/70">{t('portal.openBalance')}</div><div className="font-bold" data-testid="portal-open-balance">{loading ? '…' : fmt(balance)}</div></div>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-5">
        <Card className="p-5 border-slate-200">
          <h3 className="font-bold text-slate-900">{t('portal.recentInvoices')}</h3>
          <div className="mt-3 space-y-2">
            {loading ? <div className="text-sm text-slate-400 p-3">{t('portal.inv.loading')}</div>
              : recent.length === 0 ? <div className="text-sm text-slate-400 p-3">{t('portal.inv.none')}</div>
              : recent.map(inv => (
              <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50">
                <div><div className="font-semibold text-slate-900 text-sm">{invoiceLabel(inv)}</div><div className="text-xs text-slate-500">{inv.date || ''}</div></div>
                <div className="flex items-center gap-2"><Badge className={`${isPaid(inv) ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'} border-0`}>{inv.status || t('portal.inv.statusOpen')}</Badge><span className="font-bold tabular-nums w-20 text-right">{fmt(invoiceAmount(inv))}</span></div>
              </div>
            ))}
          </div>
          <Button asChild variant="outline" className="mt-3 w-full"><Link to="/portal/invoices">{t('portal.viewAll')}</Link></Button>
        </Card>

        <Card className="p-5 border-slate-200">
          <h3 className="font-bold text-slate-900">{t('portal.quickActions')}</h3>
          <div className="mt-3 space-y-2">
            <Link to="/portal/book" className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-[#8BC53F] hover:bg-[#8BC53F]/5 transition"><div className="w-10 h-10 rounded-lg bg-[#8BC53F]/10 text-[#8BC53F] flex items-center justify-center"><Calendar className="w-5 h-5"/></div><div className="flex-1"><div className="font-semibold text-slate-900">{t('portal.scheduleNew')}</div><div className="text-xs text-slate-500">{t('portal.scheduleNewSub')}</div></div><ChevronRight className="w-4 h-4 text-slate-400"/></Link>
            <Link to="/portal/invoices" className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-[#F59E0B] hover:bg-[#F59E0B]/5 transition"><div className="w-10 h-10 rounded-lg bg-[#F59E0B]/10 text-[#F59E0B] flex items-center justify-center"><CreditCard className="w-5 h-5"/></div><div className="flex-1"><div className="font-semibold text-slate-900">{t('portal.inv.title')}</div><div className="text-xs text-slate-500">{t('portal.inv.payNow')}</div></div><ChevronRight className="w-4 h-4 text-slate-400"/></Link>
            <Link to="/portal/history" className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-400 transition"><div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"><History className="w-5 h-5"/></div><div className="flex-1"><div className="font-semibold text-slate-900">{t('portal.viewHistory')}</div><div className="text-xs text-slate-500">{t('portal.viewHistorySub')}</div></div><ChevronRight className="w-4 h-4 text-slate-400"/></Link>
          </div>
        </Card>
      </div>

      <Card className="p-5 border-slate-200" data-testid="portal-upcoming">
        <h3 className="font-bold text-slate-900">{t('portal.upcoming')}</h3>
        <div className="mt-3 space-y-2">
          {loading ? <div className="text-sm text-slate-400">{t('portal.inv.loading')}</div>
            : upcoming.length === 0 ? <div className="text-sm text-slate-400">{t('portal.noUpcoming')}</div>
            : upcoming.slice(0, 5).map((j) => (
              <div key={j.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-100">
                <div>
                  <div className="font-semibold text-slate-900 text-sm">{j.service || j.name || 'Service'}</div>
                  <div className="text-xs text-slate-500">{prettyDate(normDate(j.date))}{j.time ? ` · ${j.time}` : ''}{j.status ? ` · ${j.status}` : ''}</div>
                </div>
                <Button asChild size="sm" variant="outline"><Link to={`/portal/book?reschedule=${encodeURIComponent(j.id)}`} data-testid={`reschedule-${j.id}`}>{t('portal.reschedule')}</Link></Button>
              </div>
            ))}
        </div>
      </Card>

      {techName && (
        <Card className="p-5 border-slate-200">
          <h3 className="font-bold text-slate-900 mb-3">{t('portal.yourTech')}</h3>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#8BC53F] to-[#6FA82C] text-white text-lg font-bold flex items-center justify-center">
              {techName.split(/\s+/).map((w) => w.charAt(0)).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="font-bold text-slate-900">{techName}</div>
              <div className="text-sm text-slate-500">{t('portal.leadTech')}</div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function BookService({ user }) {
  const { t } = useLang();
  const [params] = useSearchParams();
  const rescheduleId = params.get('reschedule') || '';
  const { jobs } = usePortalData();
  const moving = rescheduleId ? jobs.find((j) => j.id === rescheduleId) : null;
  const [date, setDate] = useState(shiftDays(todayISO(), 7));
  const [type, setType] = useState('quarterly');
  const [time, setTime] = useState('10:00');
  const [notes, setNotes] = useState('');
  const [address, setAddress] = useState(user?.address || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [sending, setSending] = useState(false);
  // Prefill from the account on file.
  useEffect(() => {
    portalApi.profile().then((p) => {
      setAddress((a) => a || [p.address, p.city, p.state, p.zip].filter(Boolean).join(', '));
      setPhone((v) => v || p.phone || '');
    }).catch(() => {});
  }, []);
  // Sent to the office as a request (it lands under Leads, marked "Customer
  // portal", and in the team channel). Nothing is booked until the office
  // confirms a time.
  const submit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { toast.error(t('portal.book.phone')); return; }
    setSending(true);
    try {
      await portalApi.request({
        kind: moving ? 'reschedule' : 'service', workOrderId: moving ? moving.id : '',
        service: moving ? (moving.service || moving.name || '') : t('portal.svc.' + type),
        preferredDate: date, preferredTime: time,
        phone: phone.trim(), address: address.trim(), notes: notes.trim(),
      });
      toast.success(t('portal.book.success'));
      setNotes('');
    } catch {
      toast.error(t('portal.book.error'));
    } finally { setSending(false); }
  };
  return (
    <Card className="p-6 border-slate-200">
      <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">{moving ? t('portal.reschedule') : t('portal.book.title')}</h2>
      <p className="text-slate-500 text-sm mt-1">{moving
        ? `${moving.service || moving.name || 'Service'} · ${prettyDate(normDate(moving.date))}`
        : t('portal.book.subtitle')}</p>
      <form onSubmit={submit} className="mt-6 space-y-4 max-w-xl">
        {!moving && <div><Label>{t('portal.book.serviceType')}</Label><Select value={type} onValueChange={setType}><SelectTrigger className="mt-1.5 h-11"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="quarterly">{t('portal.svc.quarterly')}</SelectItem><SelectItem value="termite">{t('portal.svc.termite')}</SelectItem><SelectItem value="bedbug">{t('portal.svc.bedbug')}</SelectItem><SelectItem value="mosquito">{t('portal.svc.mosquito')}</SelectItem><SelectItem value="rodent">{t('portal.svc.rodent')}</SelectItem></SelectContent></Select></div>}
        <div className="grid grid-cols-2 gap-3">
          <div><Label>{t('portal.book.date')}</Label><Input type="date" value={date} onChange={e=>setDate(e.target.value)} className="mt-1.5 h-11"/></div>
          <div><Label>{t('portal.book.time')}</Label><Select value={time} onValueChange={setTime}><SelectTrigger className="mt-1.5 h-11"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="08:00">8:00 AM</SelectItem><SelectItem value="10:00">10:00 AM</SelectItem><SelectItem value="12:00">12:00 PM</SelectItem><SelectItem value="14:00">2:00 PM</SelectItem><SelectItem value="16:00">4:00 PM</SelectItem></SelectContent></Select></div>
        </div>
        <div><Label>{t('portal.book.address')}</Label><Input value={address} onChange={e=>setAddress(e.target.value)} className="mt-1.5 h-11"/></div>
        <div><Label>{t('portal.book.phone')}</Label><Input type="tel" value={phone} onChange={e=>setPhone(e.target.value)} className="mt-1.5 h-11" required/></div>
        <div><Label>{t('portal.book.notes')}</Label><textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3} className="mt-1.5 w-full text-sm border border-slate-200 rounded-md p-3" placeholder={t('portal.book.notesPh')}/></div>
        <Button type="submit" disabled={sending} className="w-full h-12 bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5 font-bold"><CheckCircle2 className="w-4 h-4"/>{sending ? t('portal.book.sending') : t('portal.book.confirm')}</Button>
      </form>
    </Card>
  );
}

function PortalInvoices() {
  const { t } = useLang();
  const { invoices: rows, loading } = usePortalData();
  const invoices = [...rows].sort(byDateDesc);
  const [paying, setPaying] = useState(null);

  const pay = async (inv) => {
    setPaying(inv.id);
    try {
      const { url } = await payments.createSession(inv.id);
      window.location.href = url; // redirect to Stripe Checkout
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('portal.inv.errPay'));
      setPaying(null);
    }
  };

  return (
    <Card className="border-slate-200">
      <div className="p-5 border-b border-slate-200"><h2 className="text-xl font-extrabold tracking-tight text-slate-900">{t('portal.inv.title')}</h2></div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider"><tr><th className="px-5 py-3 text-left">{t('portal.inv.colInvoice')}</th><th className="px-5 py-3 text-left">{t('portal.inv.colDate')}</th><th className="px-5 py-3 text-left">{t('portal.inv.colStatus')}</th><th className="px-5 py-3 text-right">{t('portal.inv.colAmount')}</th><th className="px-5 py-3"></th></tr></thead>
        <tbody className="divide-y divide-slate-100">
          {loading ? <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">{t('portal.inv.loading')}</td></tr> :
            invoices.length === 0 ? <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">{t('portal.inv.none')}</td></tr> :
            invoices.map(inv => {
              const owed = outstanding(inv);
              return (
                <tr key={inv.id} className="hover:bg-slate-50" data-testid={`portal-invoice-${inv.id}`}>
                  <td className="px-5 py-4 font-semibold text-[#8BC53F]">{invoiceLabel(inv)}</td>
                  <td className="px-5 py-4 text-slate-600">{inv.date || '—'}</td>
                  <td className="px-5 py-4"><Badge className={`${isPaid(inv) ? 'bg-emerald-100 text-emerald-700' : inv.status === 'Overdue' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'} border-0`}>{inv.status || t('portal.inv.statusOpen')}</Badge></td>
                  <td className="px-5 py-4 text-right font-bold tabular-nums">{fmt(invoiceAmount(inv))}</td>
                  <td className="px-5 py-4 text-right">{owed > 0
                    ? <Button size="sm" disabled={paying === inv.id} onClick={() => pay(inv)} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid={`pay-invoice-${inv.id}`}>{paying === inv.id ? t('portal.inv.redirecting') : t('portal.inv.payNow')}</Button>
                    : null}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </Card>
  );
}

function PortalHistory() {
  const { t } = useLang();
  const { jobs, loading } = usePortalData();
  const history = jobs.filter((j) => DONE.has(statusOf(j))).sort(byDateDesc);
  return (
    <Card className="p-5 border-slate-200">
      <h2 className="text-xl font-extrabold tracking-tight text-slate-900">{t('portal.hist.title')}</h2>
      <div className="mt-4 space-y-3">
        {loading ? <div className="text-sm text-slate-400">{t('portal.hist.loading')}</div>
          : history.length === 0 ? <div className="text-sm text-slate-400" data-testid="portal-history-empty">{t('portal.hist.none')}</div>
          : history.map((h) => (
          <div key={h.id} className="flex gap-4 p-4 border border-slate-100 rounded-lg">
            <div className="w-10 h-10 rounded-full bg-[#8BC53F]/10 text-[#8BC53F] flex items-center justify-center flex-shrink-0"><CheckCircle2 className="w-5 h-5"/></div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap"><div className="font-bold text-slate-900">{h.service || h.name || 'Service'}</div><span className="text-xs text-slate-500">{h.date || ''}</span></div>
              {h.tech && <div className="text-sm text-slate-600 mt-1">{t('portal.hist.tech')} {h.tech}</div>}
              {h.notes && <div className="text-sm text-slate-700 mt-2 italic">“{h.notes}”</div>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PortalPayments() {
  const { t } = useLang();
  const [rows, setRows] = useState(null);
  useEffect(() => { portalApi.payments().then((r) => setRows(Array.isArray(r) ? r : [])).catch(() => setRows([])); }, []);
  const list = [...(rows || [])].sort(byDateDesc);
  return (
    <Card className="border-slate-200 mt-5" data-testid="portal-payments">
      <div className="p-5 border-b border-slate-200"><h2 className="text-xl font-extrabold tracking-tight text-slate-900">{t('portal.pay.title')}</h2></div>
      {rows === null ? <div className="px-5 py-8 text-center text-slate-400 text-sm">{t('portal.inv.loading')}</div>
        : list.length === 0 ? <div className="px-5 py-8 text-center text-slate-400 text-sm">{t('portal.pay.none')}</div>
        : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              {list.map((p) => (
                <tr key={p.id}>
                  <td className="px-5 py-3 text-slate-600">{p.date || ''}</td>
                  <td className="px-5 py-3 text-slate-700">{p.method || p.paymentMethod || ''}{p.invoiceNumber ? ` · #${p.invoiceNumber}` : ''}</td>
                  <td className="px-5 py-3 text-right font-bold tabular-nums">{fmt(Number(p.amount ?? p.total ?? 0) || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </Card>
  );
}

function PortalDocuments() {
  const { t } = useLang();
  const [rows, setRows] = useState(null);
  useEffect(() => { portalApi.documents().then((r) => setRows(Array.isArray(r) ? r : [])).catch(() => setRows([])); }, []);
  return (
    <Card className="p-5 border-slate-200" data-testid="portal-documents">
      <h2 className="text-xl font-extrabold tracking-tight text-slate-900">{t('portal.docs.title')}</h2>
      <div className="mt-4 space-y-2">
        {rows === null ? <div className="text-sm text-slate-400">{t('portal.inv.loading')}</div>
          : rows.length === 0 ? <div className="text-sm text-slate-400">{t('portal.docs.none')}</div>
          : rows.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-3 p-3 border border-slate-100 rounded-lg">
              <div>
                <div className="font-semibold text-slate-900 text-sm">{d.title || d.kind}</div>
                <div className="text-xs text-slate-500">{d.status}{d.signedAt ? ` · ${String(d.signedAt).slice(0, 10)}` : d.createdAt ? ` · ${String(d.createdAt).slice(0, 10)}` : ''}</div>
              </div>
              {d.signPath
                ? <Button asChild size="sm" className="bg-[#8BC53F] hover:bg-[#6FA82C]"><Link to={d.signPath}>{t('portal.docs.sign')}</Link></Button>
                : <Badge className="border-0 bg-slate-100 text-slate-700">{d.status}</Badge>}
            </div>
          ))}
      </div>
    </Card>
  );
}

function PortalProfile() {
  const { t } = useLang();
  const [p, setP] = useState(null);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    portalApi.profile().then((r) => { setP(r); setPhone(r.phone || ''); setEmail(r.email || ''); })
      .catch((e) => setP({ error: e?.response?.data?.detail || t('portal.profile.error') }));
  }, []); // eslint-disable-line
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await portalApi.updateProfile({ phone, email });
      setP(r); toast.success(t('portal.profile.saved'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('portal.profile.error'));
    } finally { setSaving(false); }
  };
  if (!p) return <Card className="p-5 border-slate-200 text-sm text-slate-400">{t('portal.inv.loading')}</Card>;
  if (p.error) return <Card className="p-5 border-slate-200 text-sm text-slate-500">{p.error}</Card>;
  const addr = [p.address, p.city, p.state, p.zip].filter(Boolean).join(', ');
  return (
    <Card className="p-6 border-slate-200" data-testid="portal-profile">
      <h2 className="text-xl font-extrabold tracking-tight text-slate-900">{t('portal.profile.title')}</h2>
      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
        <div><div className="text-xs text-slate-500">{t('portal.accountNo')}</div><div className="font-semibold">{p.accountNumber || '—'}</div></div>
        <div><div className="text-xs text-slate-500">{t('portal.book.address')}</div><div className="font-semibold">{addr || '—'}</div></div>
      </div>
      <form onSubmit={save} className="mt-5 space-y-4 max-w-md">
        <div><Label>{t('portal.book.phone')}</Label><Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 h-11" /></div>
        <div><Label>{t('portal.profile.email')}</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" /></div>
        <p className="text-xs text-slate-500">{t('portal.profile.addressNote')}</p>
        <Button type="submit" disabled={saving} className="bg-[#8BC53F] hover:bg-[#6FA82C]">{saving ? t('portal.book.sending') : t('portal.profile.save')}</Button>
      </form>
    </Card>
  );
}

export default function CustomerPortal() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  useEffect(() => { const raw = localStorage.getItem('pp_user'); if (!raw) { navigate('/login'); return; } let u; try { u = JSON.parse(raw); } catch { localStorage.removeItem('pp_user'); navigate('/login'); return; } if (!u || typeof u !== 'object') { localStorage.removeItem('pp_user'); navigate('/login'); return; } setUser(u); }, [navigate]);
  const logout = () => { localStorage.removeItem('pp_user'); navigate('/'); };
  if (!user) return null;
  return (
    <PortalShell user={user} onLogout={logout}>
      <Routes>
        <Route index element={<Overview user={user}/>}/>
        <Route path="book" element={<BookService user={user}/>}/>
        <Route path="messages" element={
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
            <MessagingView heightClass="h-[calc(100vh-160px)]"/>
          </div>
        }/>
        <Route path="invoices" element={<><PortalInvoices/><PortalPayments/></>}/>
        <Route path="history" element={<PortalHistory/>}/>
        <Route path="documents" element={<PortalDocuments/>}/>
        <Route path="profile" element={<PortalProfile/>}/>
      </Routes>
    </PortalShell>
  );
}
